import React, { useState, useRef, useEffect } from "react";
import { Modal, Form, Button, Spin, message } from "antd";
import btnStyles from "../../../../styles/Custom/Button.module.css";
import useQuery from "../../../../hooks/useQuery";
import AiVoiceRecording from "./AiVoiceRecording";
import AiAddForm from "./AiAddForm";
import {
  addIoMonitoringData,
  addVitalsMonitoringApi,
  processAudioGrbsApi,
  processAudioIoMonitoringApi,
  processAudioVitalsApi,
} from "../../../../services/nurse.services";
import dayjs from "dayjs";
import useCompactMonitoringLayout from "../../../../hooks/useCompactMonitoringLayout";
import { NURSE_IP_COMPACT_MODAL_WRAP } from "../../../IpEMR/Doctor/InitialInspection/ClosePopup";
import AiIoAddForm from "./AiIoAddForm";

const AiIoRecording = ({
  open,
  type,
  date,
  refresh,
  setOpen,
  startRecord,
  setStartRecord,
}) => {
  const [recordedFile, setRecordedFile] = useState(null);
  const [aiResponse, setAiResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formStep, setFormStep] = useState("record"); // "record" or "vitals"
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const params = useQuery();
  const stopRecordingRef = useRef(null);
  const [hourlyIntake, setHourlyIntake] = useState(0);
  const [hourlyOutput, setHourlyOutput] = useState(0);
  const isCompact = useCompactMonitoringLayout();

  useEffect(() => {
    const aiData = aiResponse || {}; // new ai data
    const hourlyIntake =
      (aiData?.oral_tube_intake || 0) +
      (aiData?.flush || 0) +
      (aiData?.iv_fluids || 0);

    const hourlyOutput =
      (aiData?.uo || 0) + (aiData?.gi_losses || 0) + (aiData?.drain || 0);

    setHourlyIntake(hourlyIntake);
    setHourlyOutput(hourlyOutput);
    form.setFieldsValue({
      ...aiData,
      process_time: aiData?.process_time
        ? dayjs.utc(aiData.process_time)
        : null,
    });
  }, [aiResponse]);

  // Reset modal data when opened/closed
  useEffect(() => {
    if (open) {
      setRecordedFile(null);
      setAiResponse(null);
      setFormStep("record");
      form.resetFields();

      // Start recording after a brief delay
      setTimeout(() => {
        setStartRecord(true);
      }, 100);
    } else {
      setStartRecord(false);
      if (stopRecordingRef.current) {
        stopRecordingRef.current();
      }
    }
  }, [open]);

  const calculateValues = (changedValues, allValues) => {
    const newHourlyIntake =
      (allValues?.oral_tube_intake || 0) +
      (allValues?.flush || 0) +
      (allValues?.iv_fluids || 0);

    const newHourlyOutput =
      (allValues?.uo || 0) +
      (allValues?.gi_losses || 0) +
      (allValues?.drain || 0);

    setHourlyIntake(newHourlyIntake);
    setHourlyOutput(newHourlyOutput);
  };

  const onValuesChange = (changedValues, allValues) => {
    calculateValues(changedValues, allValues);
  };

  const onSubmit = async (values) => {
    if (!recordedFile) {
      message.error("No recording found");
      return;
    }

    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    setLoading(true);
    const combinedDateTime = null;

    try {
      const fd = new FormData();
      fd.append("audio_file", recordedFile);
      fd.append("action_time", JSON.stringify(combinedDateTime));
      const response = await processAudioIoMonitoringApi(fd);

      setFormStep("vitals");
      setAiResponse(response?.data || {});

      message.success("Audio processed successfully!");
    } catch (error) {
      console.error(error);
      message.error("Failed to process audio.");
    } finally {
      setLoading(false);
    }
  };

  // Submit vital form
  const onVitalSubmit = async (values) => {
    const selectedTime = values?.process_time
      ? dayjs(values?.process_time).format("HH:mm")
      : null;
    const combinedDateTime =
      selectedTime &&
      dayjs(date)
        .set("hour", dayjs(values?.process_time).hour())
        .set("minute", dayjs(values?.process_time).minute())
        .format("YYYY-MM-DDTHH:mm:ss");

    const updatedValues = {
      ...values,
      action_time: combinedDateTime,
      hourly_intake: hourlyIntake || 0,
      hourly_output: hourlyOutput || 0,
    };

    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }

    addIoMonitoringData(params?.patient, updatedValues)
      ?.then((res) => {
        message.success(res?.data?.message);
        setOpen(false);
        refresh();
      })
      .catch((err) => {
        message?.error(err?.response?.data?.message);
      });
  };

  const handleCancel = () => {
    setStartRecord(false);
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
    }
    setOpen(false);
  };

  return (
    <Modal
      open={open}
      onCancel={isCompact ? handleCancel : undefined}
      closeIcon={isCompact ? undefined : null}
      title={
        formStep === "record"
          ? "Recorder"
          : "Add IO Monitoring"
      }
      width={isCompact ? "calc(100vw - 32px)" : "min(720px, calc(100vw - 48px))"}
      centered
      wrapClassName={isCompact ? NURSE_IP_COMPACT_MODAL_WRAP : undefined}
      getContainer={isCompact ? () => document.body : undefined}
      className={
        formStep === "record"
          ? "nurse-monitoring-recorder-modal"
          : "nurse-monitoring-vitals-modal nurse-monitoring-io-modal"
      }
      maskClosable={false}
      keyboard={false}
      destroyOnClose={isCompact || undefined}
      footer={
        <div className="flex-end" style={{ gap: isCompact ? 12 : 24 }}>
          <Form.Item>
            <Button
              type="primary"
              ghost
              className={btnStyles.mdBtn}
              onClick={handleCancel}
              disabled={loading}
            >
              Cancel
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="submit"
              type="primary"
              className={btnStyles.mdBtn}
              disabled={loading}
              onClick={() => {
                if (formStep === "record") {
                  onSubmit();
                } else if (formStep === "vitals" && formRef.current) {
                  formRef.current.submit();
                }
              }}
            >
              Submit
            </Button>
          </Form.Item>
        </div>
      }
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" tip="Processing..." />
        </div>
      ) : (
        <Form
          layout="vertical"
          ref={formRef}
          form={form}
          onFinish={onVitalSubmit}
          onValuesChange={onValuesChange} // Trigger calculation on field change
        >
          {/* 🎙 Step 1: Voice Recorder */}
          {formStep === "record" && (
            <AiVoiceRecording
              recordedFile={recordedFile}
              setRecordedFile={setRecordedFile}
              startRecord={startRecord}
              setStartRecord={setStartRecord}
              stopAllRecordings={stopRecordingRef}
              type={type}
              addForm={true}
            />
          )}

          {/* 💊 Step 2: AI vital Add Form */}
          {formStep === "vitals" && (
            <>
              <AiIoAddForm
                form={form}
                type={type}
                hourlyIntake={hourlyIntake}
                hourlyOutput={hourlyOutput}
              />
            </>
          )}
        </Form>
      )}
    </Modal>
  );
};

export default AiIoRecording;
